import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePagination, useTable } from 'react-table';

import styles from '../../styles/DataTable.module.scss';

const getValue = (column, row) => {
	if (column.sortValue) return column.sortValue(row);
	if (column.accessor) return column.accessor(row);
	return '';
};

const normalise = (value) => {
	if (value === null || value === undefined) return '';
	if (value instanceof Date) return value.toISOString();
	return String(value).toLowerCase();
};

function DataTable({
	title,
	description,
	columns,
	data,
	emptyMessage = 'No records to show.',
	searchPlaceholder = 'Search',
	toolbarStart = null,
	filters = null,
	filtersOpen = true,
	filterButton = null,
	actions = null,
	queryPrefix = 'table',
	initialSort = '',
	initialDirection = 'asc',
	rowClassName = () => '',
	getRowKey = row => row._id,
	defaultPageSize = 10
}) {
	const [searchParams, setSearchParams] = useSearchParams();
	const key = name => `${queryPrefix}_${name}`;

	const search = searchParams.get(key('search')) || '';
	const sort = searchParams.get(key('sort')) || initialSort;
	const direction = searchParams.get(key('direction')) || initialDirection;
	const pageNumber = Math.max(parseInt(searchParams.get(key('page')) || '1', 10), 1);
	const pageSize = Math.max(parseInt(searchParams.get(key('pageSize')) || String(defaultPageSize), 10), 1);

	const setParam = (name, value, resetPage = true) => {
		const next = new URLSearchParams(searchParams);
		if (value === null || value === undefined || value === '') {
			next.delete(key(name));
		} else {
			next.set(key(name), value);
		}
		if (resetPage) next.set(key('page'), '1');
		if (next.toString() === searchParams.toString()) return;
		setSearchParams(next);
	};

	const searchableColumns = useMemo(() => columns.filter(column => column.search !== false), [columns]);

	const filtered = useMemo(() => {
		const searchTerm = search.trim().toLowerCase();
		if (!searchTerm) return data;

		return data.filter(row => searchableColumns.some(column => {
			const value = column.searchValue ? column.searchValue(row) : getValue(column, row);
			return normalise(value).includes(searchTerm);
		}));
	}, [data, search, searchableColumns]);

	const sorted = useMemo(() => {
		if (!sort) return filtered;
		const column = columns.find(item => item.id === sort);
		if (!column) return filtered;

		return [...filtered].sort((a, b) => {
			const aValue = getValue(column, a);
			const bValue = getValue(column, b);
			const aDate = Date.parse(aValue);
			const bDate = Date.parse(bValue);
			let result;

			if (!Number.isNaN(aDate) && !Number.isNaN(bDate)) {
				result = aDate - bDate;
			} else if (typeof aValue === 'number' && typeof bValue === 'number') {
				result = aValue - bValue;
			} else {
				result = normalise(aValue).localeCompare(normalise(bValue));
			}

			return direction === 'desc' ? result * -1 : result;
		});
	}, [columns, direction, filtered, sort]);

	const totalPages = Math.max(Math.ceil(sorted.length / pageSize), 1);
	const currentPage = Math.min(pageNumber, totalPages);

	const tableColumns = useMemo(() => columns.map(column => ({
		id: column.id,
		Header: column.header,
		accessor: row => getValue(column, row),
		Cell: ({ row }) => column.cell ? column.cell(row.original) : getValue(column, row.original),
		disableSortBy: column.sortable === false
	})), [columns]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		page: tablePage,
		prepareRow,
		gotoPage,
		setPageSize
	} = useTable(
		{
			columns: tableColumns,
			data: sorted,
			initialState: {
				pageIndex: currentPage - 1,
				pageSize
			},
			manualSortBy: true,
			autoResetPage: false
		},
		usePagination
	);

	useEffect(() => {
		setPageSize(pageSize);
	}, [pageSize, setPageSize]);

	useEffect(() => {
		gotoPage(currentPage - 1);
	}, [currentPage, gotoPage]);

	const changeSort = (column) => {
		if (!column) return;
		if (column.sortable === false) return;
		if (sort === column.id) {
			setParam('direction', direction === 'asc' ? 'desc' : 'asc');
			return;
		}
		const next = new URLSearchParams(searchParams);
		next.set(key('sort'), column.id);
		next.set(key('direction'), 'asc');
		next.set(key('page'), '1');
		setSearchParams(next);
	};

	return (
		<section className={styles.tableCard}>
			<div className={styles.header}>
				<div>
					<h2>{title}</h2>
					{description && <p>{description}</p>}
				</div>
				{actions && <div className={styles.actions}>{actions}</div>}
			</div>

			<div className={styles.toolbar}>
				{toolbarStart && <div className={styles.toolbarStart}>{toolbarStart}</div>}
				<div className={styles.search}>
					<span className={styles.searchIcon} />
					<input
						type="search"
						value={search}
						placeholder={searchPlaceholder}
						onChange={(event) => setParam('search', event.target.value)}
					/>
				</div>
				{filterButton && <div className={styles.filterButton}>{filterButton}</div>}
				{filters && filtersOpen && <div className={styles.filters}>{filters}</div>}
			</div>

			<div className={styles.tableWrap}>
				<table {...getTableProps()}>
					<thead>
						{headerGroups.map(headerGroup => (
							<tr {...headerGroup.getHeaderGroupProps()}>
								{headerGroup.headers.map(column => {
									const sourceColumn = columns.find(item => item.id === column.id);
									return (
										<th {...column.getHeaderProps()}>
											<button
												type="button"
												onClick={() => changeSort(sourceColumn)}
												disabled={sourceColumn?.sortable === false}
											>
												<span>{column.render('Header')}</span>
												{sourceColumn?.sortable !== false && (
													<span className={styles.sortIndicator}>
														{sort === sourceColumn?.id ? (direction === 'asc' ? '^' : 'v') : '-'}
													</span>
												)}
											</button>
										</th>
									);
								})}
							</tr>
						))}
					</thead>
					<tbody {...getTableBodyProps()}>
						{tablePage.map(row => {
							prepareRow(row);
							return (
								<tr {...row.getRowProps({ key: getRowKey(row.original) })} className={rowClassName(row.original)}>
									{row.cells.map(cell => (
										<td {...cell.getCellProps()} data-label={cell.column.Header}>
											{cell.render('Cell')}
										</td>
									))}
								</tr>
							);
						})}
					</tbody>
				</table>
				{!tablePage.length && <div className={styles.empty}>{emptyMessage}</div>}
			</div>
			<p className={styles.scrollHint}>Swipe left to see more →</p>

			<div className={styles.footer}>
				<p>Total: {sorted.length}</p>
				<div className={styles.pagination}>
					<button type="button" onClick={() => setParam('page', String(currentPage - 1), false)} disabled={currentPage === 1}>Prev</button>
					{Array.from({ length: Math.min(totalPages, 5) }, (_, index) => {
						const start = Math.max(Math.min(currentPage - 2, totalPages - 4), 1);
						const pageNumber = start + index;
						if (pageNumber > totalPages) return null;
						return (
							<button
								type="button"
								key={pageNumber}
								className={pageNumber === currentPage ? styles.activePage : ''}
								onClick={() => setParam('page', String(pageNumber), false)}
							>
								{pageNumber}
							</button>
						);
					})}
					<button type="button" onClick={() => setParam('page', String(currentPage + 1), false)} disabled={currentPage === totalPages}>Next</button>
				</div>
				<label>
					<span>Show per page:</span>
					<select value={pageSize} onChange={(event) => setParam('pageSize', event.target.value)}>
						<option value="10">10</option>
						<option value="25">25</option>
						<option value="50">50</option>
					</select>
				</label>
			</div>
		</section>
	);
}

export default DataTable;
